import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { db } from '../lib/supabase'

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState({ tiendas: [], eventos: [] })
  const [searching, setSearching] = useState(false)
  const [currentTime, setCurrentTime] = useState(null)
  const [isOpen, setIsOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Get Lima time and check if mall is open
  useEffect(() => {
    const fetchLimaTime = async () => {
      try {
        const response = await fetch('https://worldtimeapi.org/api/timezone/America/Lima')
        const data = await response.json()
        const limaTime = new Date(data.datetime)
        setCurrentTime(limaTime)
        checkIfOpen(limaTime)
      } catch (error) {
        // Fallback to user's local time if API fails
        const localTime = new Date()
        setCurrentTime(localTime)
        checkIfOpen(localTime)
      }
    }

    const checkIfOpen = (time) => {
      const hours = time.getHours()
      const day = time.getDay() // 0 = Sunday, 6 = Saturday
      
      // Mall hours: Monday-Sunday 8:00 AM - 10:00 PM (22:00)
      const openHour = 8
      const closeHour = 22
      
      setIsOpen(hours >= openHour && hours < closeHour)
    }

    // Fetch time immediately
    fetchLimaTime()

    // Update time every minute
    const interval = setInterval(() => {
      fetchLimaTime()
    }, 60000) // Update every 60 seconds

    return () => clearInterval(interval)
  }, [])

  const navLinks = [
    { to: '/', label: 'Inicio' },
    { to: '/descuentos', label: 'Descuentos' },
    { to: '/marcas', label: 'Tiendas' },
    { to: '/eventos', label: 'Eventos' },
    { to: '/ubicanos', label: 'Ubícanos' },
  ]

  const isActive = (path) => {
    const basePath = path.split('?')[0]
    const currentPath = location.pathname

    // Exact match
    if (currentPath === basePath) return true

    // For "/" (Inicio), only match exact
    if (basePath === '/') return false

    // Check if current path starts with the base path (child routes)
    // /descuentos -> /descuentos/:id
    // /eventos -> /eventos/:id
    // /marcas -> /marcas/:id
    if (currentPath.startsWith(basePath + '/')) return true

    return false
  }

  // Normalize text for search (remove accents and convert to lowercase)
  const normalizeText = (text) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics/accents
  }

  // Search functionality
  const handleSearch = async (query) => {
    setSearchQuery(query)
    
    if (!query.trim()) {
      setSearchResults({ tiendas: [], eventos: [] })
      return
    }

    setSearching(true)
    try {
      const normalizedQuery = normalizeText(query)

      // Search tiendas
      const { data: tiendas } = await db.getTiendas()
      const filteredTiendas = tiendas?.filter(tienda => {
        const normalizedNombre = normalizeText(tienda.nombre)
        // Check if the normalized name includes the normalized query (partial match)
        return normalizedNombre.includes(normalizedQuery)
      }) || []

      // Search eventos
      const { data: eventos } = await db.getEventos()
      const filteredEventos = eventos?.filter(evento => {
        const normalizedNombre = normalizeText(evento.nombre)
        // Check if the normalized name includes the normalized query (partial match)
        return normalizedNombre.includes(normalizedQuery)
      }) || []

      setSearchResults({
        tiendas: filteredTiendas.slice(0, 5), // Limit to 5 results each
        eventos: filteredEventos.slice(0, 5)
      })
    } catch (error) {
      console.error('Search error:', error)
    } finally {
      setSearching(false)
    }
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    const query = searchQuery.trim().toLowerCase()
    
    if (!query) return

    // Check for general category searches
    if (query === 'tiendas' || query === 'marcas') {
      navigate('/marcas')
      setShowSearch(false)
      setSearchQuery('')
      return
    }

    if (query === 'eventos') {
      navigate('/eventos')
      setShowSearch(false)
      setSearchQuery('')
      return
    }

    // If there's an exact or close match, navigate to it
    if (searchResults.tiendas.length > 0) {
      navigate(`/marcas/${searchResults.tiendas[0].id}`)
      setShowSearch(false)
      setSearchQuery('')
      return
    }

    if (searchResults.eventos.length > 0) {
      navigate(`/eventos/${searchResults.eventos[0].id}`)
      setShowSearch(false)
      setSearchQuery('')
      return
    }

    // If no results, go to general pages
    navigate('/marcas')
    setShowSearch(false)
    setSearchQuery('')
  }

  const handleResultClick = (type, id) => {
    if (type === 'tienda') {
      navigate(`/marcas/${id}`)
    } else {
      navigate(`/eventos/${id}`)
    }
    setShowSearch(false)
    setSearchQuery('')
    setSearchResults({ tiendas: [], eventos: [] })
  }

  // Close search on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showSearch) {
        setShowSearch(false)
        setSearchQuery('')
        setSearchResults({ tiendas: [], eventos: [] })
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [showSearch])

  return (
    <header 
      role="banner"
      style={{
        padding: '0.75rem 0',
        borderBottom: '1px solid var(--color-stroke)',
        backgroundColor: 'var(--color-background)',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10000,
        transition: 'box-shadow 0.3s ease'
      }}
    >
      <div 
        className="container"
        style={{
          maxWidth: 'var(--container-max-width)',
          margin: '0 auto',
          padding: '0 2rem'
        }}
      >
        <div 
          className="header-content"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem'
          }}
        >
        {/* Logo */}
        <Link 
  to="/" 
  className="brand"
  aria-label="Plaza Aurora Home"
  style={{
    fontFamily: 'var(--font-display)',
    fontSize: '1rem',
    fontWeight: 'var(--font-weight-bold)',
    letterSpacing: '0.04em',
    color: 'var(--color-text-primary)',
    cursor: 'pointer',
    textDecoration: 'none'
  }}
>
  <img
    src="https://ffbzkwlpbswgbngwvyvp.supabase.co/storage/v1/object/public/assets/logo/PlazaAurora_Logo.png"
    alt="Plaza Aurora"
    style={{ height: '32px', width: 'auto', display: 'block' }}
  />
</Link>

        {/* Desktop Navigation (hidden on mobile) */}
        {!isMobile && (
          <nav 
            className="mainNav"
            style={{
              display: 'flex',
              gap: '1.5rem',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-body)'
            }}
          >
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`nav-link ${isActive(link.to) ? 'active' : ''}`}
                style={{
                  color: isActive(link.to) ? 'var(--brand-blue)' : 'var(--color-text-muted)',
                  transition: 'color 0.2s ease, background-color 0.2s ease',
                  position: 'relative',
                  textDecoration: 'none',
                  paddingLeft: isActive(link.to) ? '1rem' : '0.5rem'
                }}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        )}

        {/* Time and Status Indicator (hidden on mobile) */}
        {!isMobile && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 0.75rem',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-accent-gray)',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-small)',
            minWidth: '160px', // Reserve space
            visibility: currentTime ? 'visible' : 'hidden' // Hidden but takes space
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem'
            }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              <span style={{ color: 'var(--color-text-primary)', fontWeight: 'var(--font-weight-medium)' }}>
                {currentTime ? currentTime.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit', hour12: true }) : '00:00'}
              </span>
            </div>
            <div style={{
              width: '1px',
              height: '16px',
              background: 'var(--color-stroke)'
            }}></div>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.35rem'
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: isOpen ? '#0ACC66' : '#EF4444',
                boxShadow: isOpen ? '0 0 6px rgba(10, 204, 102, 0.5)' : '0 0 6px rgba(239, 68, 68, 0.5)'
              }}></div>
              <span style={{
                color: isOpen ? '#0ACC66' : '#EF4444',
                fontWeight: 'var(--font-weight-bold)',
                fontSize: 'var(--font-size-small)'
              }}>
                {isOpen ? 'Abierto' : 'Cerrado'}
              </span>
            </div>
          </div>
        )}

        {/* Header Actions */}
        <div className="headerActions" style={{
          display: 'flex',
          gap: '0.75rem',
          alignItems: 'center'
        }}>
          <button 
            className="icon-btn" 
            aria-label="Search" 
            onClick={() => setShowSearch(true)}
            style={{
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              borderRadius: 'var(--radius-sm)',
              transition: 'background-color 0.2s ease, transform 0.2s ease',
              background: 'none',
              border: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--brand-blue-50)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
            }}
            onMouseDown={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--brand-blue-100)'
            }}
            onTouchStart={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--brand-blue-50)'
              e.currentTarget.style.transform = 'scale(0.95)'
            }}
            onTouchEnd={(e) => {
              setTimeout(() => {
                e.currentTarget.style.backgroundColor = 'transparent'
                e.currentTarget.style.transform = 'scale(1)'
              }, 150)
            }}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="9" cy="9" r="5" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M13 13L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </button>

          {/* Mobile Hamburger Menu Toggle */}
          {isMobile && (
            <button
              aria-label="Abrir menú"
              onClick={() => setMenuOpen(prev => !prev)}
              style={{
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--brand-blue)',
                border: '1px solid var(--brand-blue)',
                transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--brand-blue-600)'
                e.currentTarget.style.borderColor = 'var(--brand-blue-600)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--brand-blue)'
                e.currentTarget.style.borderColor = 'var(--brand-blue)'
              }}
              onTouchStart={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--brand-blue-600)'
                e.currentTarget.style.borderColor = 'var(--brand-blue-600)'
                e.currentTarget.style.transform = 'scale(0.95)'
              }}
              onTouchEnd={(e) => {
                setTimeout(() => {
                  e.currentTarget.style.backgroundColor = 'var(--brand-blue)'
                  e.currentTarget.style.borderColor = 'var(--brand-blue)'
                  e.currentTarget.style.transform = 'scale(1)'
                }, 150)
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6H21" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                <path d="M3 12H21" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                <path d="M3 18H21" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {isMobile && menuOpen && (
        <div
          style={{
            position: 'fixed',
            top: '56px',
            left: 0,
            right: 0,
            backgroundColor: 'var(--color-background)',
            borderTop: '1px solid var(--color-stroke)',
            zIndex: 1000,
            padding: '1rem',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem'
          }}
        >
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setMenuOpen(false)}
              style={{
                padding: '0.75rem 1rem',
                paddingLeft: '2.5rem',
                border: `1px solid ${isActive(link.to) ? 'var(--brand-blue)' : 'var(--color-stroke)'}`,
                borderRadius: 'var(--radius-sm)',
                textDecoration: 'none',
                color: isActive(link.to) ? 'white' : 'var(--color-text-muted)',
                fontFamily: 'var(--font-ui)',
                fontWeight: isActive(link.to) ? 'var(--font-weight-bold)' : 'var(--font-weight-normal)',
                background: isActive(link.to) ? 'var(--brand-blue)' : 'transparent',
                transition: 'all 0.2s ease',
                position: 'relative',
                display: 'block'
              }}
            >
              {isActive(link.to) && (
                <img
                  src="https://ffbzkwlpbswgbngwvyvp.supabase.co/storage/v1/object/public/assets/logo/favicons/Indicador_blanco.png"
                  alt=""
                  style={{
                    position: 'absolute',
                    left: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: '16px',
                    height: '16px',
                    objectFit: 'contain'
                  }}
                />
              )}
              {link.label}
            </Link>
          ))}
        </div>
      )}

      {/* Search Modal */}
      {showSearch && (
        <div 
          className="search-modal-overlay"
          onClick={() => setShowSearch(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            paddingTop: '5rem'
          }}
        >
          <div 
            className="search-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: 'var(--color-background)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Search Header */}
            <div style={{
              padding: '1.5rem',
              borderBottom: '1px solid var(--color-stroke)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h2 style={{
                fontFamily: 'var(--font-display)',
                fontSize: 'var(--font-size-h3)',
                fontWeight: 'var(--font-weight-bold)',
                color: 'var(--color-text-primary)',
                margin: 0
              }}>Buscar</h2>
              <button
                onClick={() => setShowSearch(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--color-text-muted)',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 'var(--radius-sm)',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-accent-gray)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                ×
              </button>
            </div>

            {/* Search Input */}
            <form onSubmit={handleSearchSubmit} style={{ padding: '1.5rem' }}>
              <input
                type="text"
                placeholder="Buscar tiendas, eventos..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                autoFocus
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  fontSize: 'var(--font-size-body)',
                  fontFamily: 'var(--font-ui)',
                  border: '1px solid var(--color-stroke)',
                  borderRadius: 'var(--radius-sm)',
                  outline: 'none',
                  transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--brand-blue)'
                  e.currentTarget.style.boxShadow = '0 0 0 3px var(--brand-blue-200)'
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-stroke)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              />
              <p style={{
                marginTop: '0.5rem',
                fontSize: 'var(--font-size-small)',
                color: 'var(--color-text-muted)',
                fontFamily: 'var(--font-ui)'
              }}>
                Escribe "tiendas" o "eventos" para ver todas, o busca por nombre específico
              </p>
            </form>

            {/* Search Results */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '0 1.5rem 1.5rem'
            }}>
              {searching ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <p style={{ color: 'var(--color-text-muted)' }}>Buscando...</p>
                </div>
              ) : (
                <>
                  {/* Tiendas Results */}
                  {searchResults.tiendas.length > 0 && (
                    <div style={{ marginBottom: '1.5rem' }}>
                      <h3 style={{
                        fontSize: 'var(--font-size-small)',
                        fontWeight: 'var(--font-weight-bold)',
                        color: 'var(--color-text-muted)',
                        marginBottom: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>Tiendas</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {searchResults.tiendas.map((tienda) => (
                          <button
                            key={tienda.id}
                            onClick={() => handleResultClick('tienda', tienda.id)}
                            style={{
                              padding: '0.75rem',
                              background: 'var(--color-accent-gray)',
                              border: '1px solid var(--color-stroke)',
                              borderRadius: 'var(--radius-sm)',
                              cursor: 'pointer',
                              textAlign: 'left',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = 'var(--brand-blue)'
                              e.currentTarget.style.transform = 'translateX(4px)'
                              e.currentTarget.style.background = 'var(--brand-blue-50)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = 'var(--color-stroke)'
                              e.currentTarget.style.transform = 'translateX(0)'
                              e.currentTarget.style.background = 'var(--color-accent-gray)'
                            }}
                            onMouseDown={(e) => {
                              e.currentTarget.style.background = 'var(--brand-blue-100)'
                            }}
                          >
                            <span style={{ fontSize: '1.5rem' }}>🏪</span>
                            <div>
                              <div style={{
                                fontWeight: 'var(--font-weight-bold)',
                                color: 'var(--color-text-primary)',
                                marginBottom: '0.25rem'
                              }}>
                                {tienda.nombre}
                              </div>
                              {tienda.categoria?.nombre && (
                                <div style={{
                                  fontSize: 'var(--font-size-small)',
                                  color: 'var(--color-text-muted)'
                                }}>
                                  {tienda.categoria.nombre}
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Eventos Results */}
                  {searchResults.eventos.length > 0 && (
                    <div>
                      <h3 style={{
                        fontSize: 'var(--font-size-small)',
                        fontWeight: 'var(--font-weight-bold)',
                        color: 'var(--color-text-muted)',
                        marginBottom: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em'
                      }}>Eventos</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        {searchResults.eventos.map((evento) => (
                          <button
                            key={evento.id}
                            onClick={() => handleResultClick('evento', evento.id)}
                            style={{
                              padding: '0.75rem',
                              background: 'var(--color-accent-gray)',
                              border: '1px solid var(--color-stroke)',
                              borderRadius: 'var(--radius-sm)',
                              cursor: 'pointer',
                              textAlign: 'left',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.75rem'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.borderColor = 'var(--brand-orange)'
                              e.currentTarget.style.transform = 'translateX(4px)'
                              e.currentTarget.style.background = 'var(--brand-orange-50)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.borderColor = 'var(--color-stroke)'
                              e.currentTarget.style.transform = 'translateX(0)'
                              e.currentTarget.style.background = 'var(--color-accent-gray)'
                            }}
                            onMouseDown={(e) => {
                              e.currentTarget.style.background = 'var(--brand-orange-100)'
                            }}
                          >
                            <span style={{ fontSize: '1.5rem' }}>🎉</span>
                            <div>
                              <div style={{
                                fontWeight: 'var(--font-weight-bold)',
                                color: 'var(--color-text-primary)',
                                marginBottom: '0.25rem'
                              }}>
                                {evento.nombre}
                              </div>
                              <div style={{
                                fontSize: 'var(--font-size-small)',
                                color: 'var(--color-text-muted)'
                              }}>
                                {evento.estado}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* No Results */}
                  {searchQuery && !searching && 
                   searchResults.tiendas.length === 0 && 
                   searchResults.eventos.length === 0 && (
                    <div style={{
                      textAlign: 'center',
                      padding: '3rem 1rem',
                      color: 'var(--color-text-muted)'
                    }}>
                      <p style={{ marginBottom: '0.5rem' }}>No se encontraron resultados</p>
                      <p style={{ fontSize: 'var(--font-size-small)' }}>
                        Intenta con "tiendas" o "eventos" para ver todas las opciones
                      </p>
                    </div>
                  )}

                  {/* Empty State */}
                  {!searchQuery && (
                    <div style={{
                      textAlign: 'center',
                      padding: '3rem 1rem',
                      color: 'var(--color-text-muted)'
                    }}>
                      <svg width="48" height="48" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ margin: '0 auto 1rem' }}>
                        <circle cx="9" cy="9" r="5" stroke="currentColor" strokeWidth="1.5"/>
                        <path d="M13 13L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                      <p>Empieza a escribir para buscar tiendas o eventos</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  )
}

